/* The audio engine — one HTMLAudioElement for recitation (it is the thing iOS
   keeps alive in the background and shows on the lock screen) and a second,
   tiny one that plays generated silence for repeat-and-gap pauses.

   Why silence instead of setTimeout for gaps: with the screen locked iOS
   throttles timers in a PWA, but keeps delivering media events to an element
   that is playing. A gap made of real (silent) audio ends on time; a timer
   might fire minutes later. */
import { create } from 'zustand';
import { reciterById } from '@/data/reciters';
import { chapter } from '@/features/quran/api';
import { useProgress, useSettings } from '@/lib/store';
import { canHear } from './access';
import { ayahIndex, firstSurahOfJuz, loadTrack, locate, type Track } from './timings';

export type PlayerStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'gap' | 'error';

interface Range { from: number; to: number } // ayah numbers within the surah

interface PlayerState {
  status: PlayerStatus;
  track: Track | null;
  surah: number | null;
  reciterId: string;
  range: Range | null;
  current: { verseKey: string | null; word: number | null };
  ayahPass: number;
  rangePass: number;
  positionMs: number;
  durationMs: number;
  error: string | null;
  sheetOpen: boolean;
  /** bumped each time an āyah range plays through to its end, repeats included (not when paused by hand) */
  rangeDone: number;
  /** startAtMs: resume at an exact position (overrides fromAyah's start). */
  play: (surah: number, opts?: { fromAyah?: number; toAyah?: number; reciterId?: string; juz?: number; clip?: string; startAtMs?: number }) => Promise<void>;
  toggle: () => void;
  pause: () => void;
  stop: () => void;
  seekAyah: (verseKey: string) => void;
  seekMs: (ms: number) => void;
  nextAyah: () => void;
  prevAyah: () => void;
  setRange: (r: Range | null) => void;
  setReciter: (id: string) => Promise<void>;
  setSheet: (open: boolean) => void;
  setReciteMode: (on: boolean) => void;
  skipGap: () => void;
}

let audio: HTMLAudioElement | null = null;
let gapAudio: HTMLAudioElement | null = null;
let raf = 0;
let listenStart = 0;
let listenAccum = 0;

/* One animation loop at a time — tick is also called from timeupdate while
   hidden, and every caller reschedules. */
const schedule = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(tick); };

const el = () => {
  if (!audio) {
    audio = new Audio();
    audio.preload = 'auto';
    (audio as HTMLAudioElement & { playsInline?: boolean }).playsInline = true;
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('pause', () => {
      /* Reaching the end fires 'pause' just before 'ended' — leave the status
         alone so onEnded can loop the surah or advance to the next one. */
      if (audio!.ended) return;
      const status = usePlayer.getState().status;
      if (status === 'playing') usePlayer.setState({ status: 'paused' });
      accumulate();
      /* A gap is part of the same sitting — and in recite mode it is the
         time the user is reciting, so it keeps counting. */
      if (status === 'gap' || usePlayer.getState().status === 'gap') { listenStart = Date.now(); }
      else commitListening();
    });
    audio.addEventListener('play', () => { accumulate(); listenStart = Date.now(); });
    audio.addEventListener('loadedmetadata', () => usePlayer.setState({ durationMs: (audio!.duration || 0) * 1000 }));
    audio.addEventListener('error', () => usePlayer.setState({ status: 'error', error: 'Could not load this recitation. Check your connection.' }));
  }
  return audio;
};

/* 8 kHz mono 8-bit PCM silence as a WAV blob — a 10s gap is ~80KB. */
const silenceCache = new Map<number, string>();
/** A short silent WAV (generated, cached) — also used to unlock audio on iOS. */
export function silenceUrl(ms: number): string {
  const key = Math.max(200, Math.round(ms / 100) * 100);
  if (silenceCache.has(key)) return silenceCache.get(key)!;
  const rate = 8000, n = Math.round((rate * key) / 1000);
  const buf = new ArrayBuffer(44 + n);
  const v = new DataView(buf);
  const w = (o: number, s: string) => [...s].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)));
  w(0, 'RIFF'); v.setUint32(4, 36 + n, true); w(8, 'WAVE'); w(12, 'fmt ');
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, rate, true); v.setUint32(28, rate, true); v.setUint16(32, 1, true); v.setUint16(34, 8, true);
  w(36, 'data'); v.setUint32(40, n, true);
  new Uint8Array(buf, 44).fill(128);
  const url = URL.createObjectURL(new Blob([buf], { type: 'audio/wav' }));
  silenceCache.set(key, url);
  return url;
}

function accumulate() {
  if (listenStart) { listenAccum += (Date.now() - listenStart) / 1000; listenStart = 0; }
}

function commitListening() {
  accumulate();
  if (listenAccum < 5) return;
  const st = usePlayer.getState();
  useProgress.getState().addSession({
    kind: 'listen', // recite mode included — recitation isn't tracked separately
    start: Date.now() - listenAccum * 1000,
    seconds: Math.round(listenAccum),
    reciterId: st.reciterId,
    surah: st.surah ?? undefined,
  });
  listenAccum = 0;
}

const repeatCfg = () => useSettings.getState().settings.repeat;

/* Recite mode is not a separate flag that can drift out of sync: it is ON
   whenever anything would interrupt straight playback (a pause after each
   āyah, repeats, or an āyah range). Switching it off clears all of those. */
export const isReciting = (range: Range | null = usePlayer.getState().range) => {
  const r = repeatCfg();
  return r.gapMode !== 'off' || r.ayahRepeat !== 1 || r.rangeRepeat !== 1 || !!range;
};

/* Repeat counts: 0 means "until I stop" (∞). `pass` is 0-based. */
export const INFINITE = 0;
const more = (count: number, pass: number) => count === INFINITE || pass + 1 < count;

/* The continuation of a gap in progress, so switching recite mode off (or
   skipping the gap) can carry on immediately instead of waiting it out. */
let pendingGap: (() => void) | null = null;

function gapMs(ayahDurationMs: number): number {
  const r = repeatCfg();
  switch (r.gapMode) {
    case 'x1': return ayahDurationMs / r.rate;
    case 'x1.5': return (ayahDurationMs * 1.5) / r.rate;
    case 'x2': return (ayahDurationMs * 2) / r.rate;
    case 'fixed': return r.gapSeconds * 1000;
    default: return 0;
  }
}

function rangeBounds(st: PlayerState): { first: number; last: number } {
  const t = st.track!;
  const keys = t.ayahs.map((a) => a.verseKey);
  const idxOf = (ayah: number) => keys.indexOf(`${t.surah}:${ayah}`);
  if (st.range) return { first: Math.max(0, idxOf(st.range.from)), last: Math.max(0, idxOf(st.range.to)) };
  // juz tracks contain several surahs — bound to the requested one
  const first = keys.findIndex((k) => k.startsWith(`${t.surah}:`));
  let last = first;
  while (last + 1 < keys.length && keys[last + 1].startsWith(`${t.surah}:`)) last++;
  return { first: Math.max(0, first), last };
}

/* Runs every animation frame while playing: moves the highlight, and enforces
   ayah boundaries for repeat/gap/range. timeupdate alone (~4Hz) is too coarse
   for word highlighting and lets a repeat overshoot into the next ayah. */
function tick() {
  const a = el();
  const st = usePlayer.getState();
  const t = st.track;
  if (!t || st.status !== 'playing') return;
  const ms = a.currentTime * 1000;

  if (t.granularity !== 'none' && t.ayahs.length) {
    const { ayahIdx, word } = locate(t, ms);
    const seg = t.ayahs[ayahIdx];
    const { first, last } = rangeBounds(st);
    const r = repeatCfg();
    const repeating = r.ayahRepeat !== 1 || r.gapMode !== 'off' || st.range || r.rangeRepeat !== 1;

    // Before the range (e.g. user seeked back) — jump to its start.
    if (st.range && ayahIdx < first) { a.currentTime = t.ayahs[first].start / 1000; }
    else if (repeating && seg && ms >= seg.end - 40) {
      handleAyahEnd(ayahIdx, first, last);
      return;
    } else if (seg && (seg.verseKey !== st.current.verseKey || word !== st.current.word)) {
      usePlayer.setState({ current: { verseKey: seg.verseKey, word } });
    }
  }
  if (Math.abs(ms - st.positionMs) > 250) usePlayer.setState({ positionMs: ms });
  schedule();
}

function handleAyahEnd(idx: number, first: number, last: number) {
  const a = el();
  const st = usePlayer.getState();
  const t = st.track!;
  const r = repeatCfg();
  const seg = t.ayahs[idx];

  const afterGap = (fn: () => void) => {
    const g = gapMs(seg.end - seg.start);
    if (g <= 0) { fn(); return; }
    a.pause();
    usePlayer.setState({ status: 'gap' });
    const ga = gapAudio ?? (gapAudio = new Audio());
    ga.src = silenceUrl(g);
    const go = () => { if (pendingGap !== go) return; pendingGap = null; if (usePlayer.getState().status === 'gap') { fn(); resume(); } };
    pendingGap = go;
    ga.onended = go;
    ga.play().catch(() => setTimeout(go, g));
  };

  if (more(r.ayahRepeat, st.ayahPass)) {
    afterGap(() => { a.currentTime = seg.start / 1000; usePlayer.setState({ ayahPass: st.ayahPass + 1 }); });
    if (r.gapMode === 'off') schedule();
    return;
  }
  if (idx >= last) {
    if (more(r.rangeRepeat, st.rangePass)) {
      afterGap(() => { a.currentTime = t.ayahs[first].start / 1000; usePlayer.setState({ ayahPass: 0, rangePass: st.rangePass + 1 }); });
      if (r.gapMode === 'off') schedule();
      return;
    }
    if (st.range) { finish(); return; }
    // No explicit range: let the file run on (onEnded advances to the next surah).
    usePlayer.setState({ ayahPass: 0 });
    schedule();
    return;
  }
  afterGap(() => { a.currentTime = t.ayahs[idx + 1].start / 1000; usePlayer.setState({ ayahPass: 0, current: { verseKey: t.ayahs[idx + 1].verseKey, word: null } }); });
  if (r.gapMode === 'off') schedule();
}

function resume() {
  const a = el();
  usePlayer.setState({ status: 'playing' });
  mediaSession(usePlayer.getState()); // the adhan or ruqyah may have held the lock-screen controls
  a.playbackRate = repeatCfg().rate;
  a.play().then(() => schedule()).catch(() => usePlayer.setState({ status: 'paused' }));
}

function finish() {
  el().pause();
  cancelAnimationFrame(raf);
  usePlayer.setState((st) => ({ status: 'paused', ayahPass: 0, rangePass: 0, rangeDone: st.range ? st.rangeDone + 1 : st.rangeDone }));
  commitListening();
}

function onEnded() {
  const st = usePlayer.getState();
  if (st.status !== 'playing') return; // the silent unlock clip, or a stale src
  commitListening();
  // Whole-file loop — also the only way to loop recordings without timings.
  if (!st.range && st.surah && more(repeatCfg().rangeRepeat, st.rangePass)) {
    usePlayer.setState({ rangePass: st.rangePass + 1, ayahPass: 0 });
    el().currentTime = 0;
    resume();
    return;
  }
  if (st.range || !st.surah) { finish(); return; }
  const reciter = reciterById(st.reciterId);
  if (st.track?.clip && reciter.audio === 'clips') {
    // Passage recordings: carry on to the reciter's next recording.
    const i = reciter.clips.findIndex((c) => c.id === st.track!.clip!.id);
    const next = reciter.clips[i + 1];
    if (next) void st.play(next.surah, { reciterId: st.reciterId, clip: next.id }); else finish();
    return;
  }
  if (st.track?.juz) {
    const nj = st.track.juz + 1;
    if (nj <= 30) void st.play(firstSurahOfJuz(nj), { reciterId: st.reciterId, juz: nj }); else finish();
    return;
  }
  const next = st.surah + 1;
  if (next <= 114 && (!reciter.surahs || reciter.surahs.includes(next))) void st.play(next, { reciterId: st.reciterId });
  else finish();
}

function mediaSession(st: PlayerState) {
  if (!('mediaSession' in navigator) || !st.surah) return;
  const ch = chapter(st.surah);
  const reciter = reciterById(st.reciterId);
  navigator.mediaSession.metadata = new MediaMetadata({
    title: `${ch.n} — ${ch.t}`,
    artist: reciter.name,
    album: 'For The Ummah',
    /* Square, edge-to-edge art (scripts/icons.mjs): iOS rounds Now Playing
       artwork itself, so the rounded app icon's transparent corners showed white. */
    artwork: [
      { src: `${location.origin}/icons/artwork-512.png`, sizes: '512x512', type: 'image/png' },
      { src: `${location.origin}/icons/artwork-1024.png`, sizes: '1024x1024', type: 'image/png' },
    ],
  });
  const p = usePlayer.getState;
  navigator.mediaSession.setActionHandler('play', () => p().toggle());
  navigator.mediaSession.setActionHandler('pause', () => p().pause());
  navigator.mediaSession.setActionHandler('previoustrack', () => p().prevAyah());
  navigator.mediaSession.setActionHandler('nexttrack', () => p().nextAyah());
  try { navigator.mediaSession.setActionHandler('seekto', (d) => { if (d.seekTime != null) el().currentTime = d.seekTime; }); } catch {}
}

export const usePlayer = create<PlayerState>()((set, get) => ({
  status: 'idle',
  track: null,
  surah: null,
  reciterId: useSettings.getState().settings.reciterId,
  range: null,
  current: { verseKey: null, word: null },
  ayahPass: 0,
  rangePass: 0,
  positionMs: 0,
  durationMs: 0,
  error: null,
  sheetOpen: false,
  rangeDone: 0,

  play: async (surah, opts = {}) => {
    const reciterId = opts.reciterId ?? get().reciterId ?? useSettings.getState().settings.reciterId;
    const reciter = reciterById(reciterId);
    if (!canHear(reciter, useSettings.getState().settings.gender)) {
      set({ status: 'error', error: 'Women reciters are available to sisters — set your profile in Settings → Profile.' });
      return;
    }
    if (reciter.surahs && !reciter.surahs.includes(surah) && !opts.clip) {
      set({ status: 'error', error: `${reciter.name} has not recorded ${chapter(surah).n} — choose another reciter.` });
      return;
    }
    commitListening();
    const a = el();
    a.pause();
    cancelAnimationFrame(raf);
    set({ status: 'loading', surah, reciterId, error: null, ayahPass: 0, rangePass: 0 });
    /* iOS only lets audio start inside the user gesture that asked for it.
       Loading the track is async, so "unlock" the element synchronously now
       with a silent play; the real src follows. */
    try { a.src = silenceUrl(200); void a.play().catch(() => {}); } catch {}
    try {
      const track = await loadTrack(reciter, surah, opts.juz, opts.clip);
      if (get().surah !== surah || get().reciterId !== reciterId) return; // superseded
      // fromAyah alone is just a starting point; only an explicit end makes a range.
      const range = opts.toAyah ? { from: opts.fromAyah ?? 1, to: opts.toAyah } : null;
      set({ track, range });
      a.src = track.url;
      a.playbackRate = repeatCfg().rate;
      const startKey = `${surah}:${opts.fromAyah ?? 1}`;
      const idx = ayahIndex(track, startKey);
      const startAt = opts.startAtMs != null ? opts.startAtMs / 1000 : idx >= 0 ? track.ayahs[idx].start / 1000 : 0;
      await new Promise<void>((res) => {
        if (a.readyState >= 1) return res();
        a.addEventListener('loadedmetadata', () => res(), { once: true });
        a.addEventListener('error', () => res(), { once: true });
      });
      if (startAt) a.currentTime = startAt;
      await a.play();
      set({ status: 'playing', current: { verseKey: idx >= 0 ? startKey : null, word: null } });
      mediaSession(get());
      schedule();
    } catch (e) {
      set({ status: 'error', error: e instanceof Error ? e.message : 'Playback failed' });
    }
  },

  toggle: () => {
    const st = get();
    if (st.status === 'playing') { get().pause(); return; }
    if (st.status === 'gap') { gapAudio?.pause(); pendingGap = null; set({ status: 'paused' }); return; }
    if (!st.track && st.surah) { void get().play(st.surah); return; }
    if (st.track) resume();
  },
  pause: () => { el().pause(); gapAudio?.pause(); pendingGap = null; cancelAnimationFrame(raf); set({ status: 'paused' }); },
  stop: () => {
    el().pause(); gapAudio?.pause(); cancelAnimationFrame(raf);
    commitListening();
    set({ status: 'idle', track: null, surah: null, current: { verseKey: null, word: null }, range: null, sheetOpen: false });
    if ('mediaSession' in navigator) navigator.mediaSession.metadata = null;
  },
  seekAyah: (verseKey) => {
    const st = get();
    const [s] = verseKey.split(':').map(Number);
    if (!st.track || st.surah !== s) { void get().play(s, { fromAyah: Number(verseKey.split(':')[1]), reciterId: st.reciterId }); if (st.range) set({ range: null }); return; }
    const idx = ayahIndex(st.track, verseKey);
    if (idx < 0) return;
    el().currentTime = st.track.ayahs[idx].start / 1000;
    set({ ayahPass: 0, current: { verseKey, word: null } });
    if (st.status !== 'playing') resume();
  },
  seekMs: (ms) => {
    el().currentTime = ms / 1000;
    set({ positionMs: ms, ayahPass: 0 });
  },
  nextAyah: () => {
    const st = get();
    if (!st.track || !st.current.verseKey) return;
    const idx = ayahIndex(st.track, st.current.verseKey);
    const next = st.track.ayahs[idx + 1];
    if (next) get().seekAyah(next.verseKey);
  },
  prevAyah: () => {
    const st = get();
    if (!st.track || !st.current.verseKey) return;
    const idx = ayahIndex(st.track, st.current.verseKey);
    const prev = st.track.ayahs[Math.max(0, idx - 1)];
    if (prev) get().seekAyah(prev.verseKey);
  },
  setRange: (range) => set({ range, ayahPass: 0, rangePass: 0 }),
  setReciter: async (id) => {
    useSettings.getState().update({ reciterId: id });
    const st = get();
    set({ reciterId: id });
    if (st.surah && (st.status === 'playing' || st.status === 'paused' || st.status === 'gap')) {
      const from = st.current.verseKey ? Number(st.current.verseKey.split(':')[1]) : st.range?.from;
      await get().play(st.surah, { reciterId: id, fromAyah: st.range?.from ?? from, toAyah: st.range?.to });
    }
  },
  setSheet: (sheetOpen) => set({ sheetOpen }),
  setReciteMode: (on) => {
    const s = useSettings.getState();
    const { rate, ...current } = s.settings.repeat;
    if (on) {
      s.updateDeep('repeat', { ...s.settings.reciteRepeat, gapMode: s.settings.reciteRepeat.gapMode === 'off' && s.settings.reciteRepeat.ayahRepeat === 1 ? 'x1' : s.settings.reciteRepeat.gapMode });
      return;
    }
    // Remember this setup for next time, then return to straight playback.
    if (isReciting(get().range)) s.update({ reciteRepeat: current });
    s.updateDeep('repeat', { ayahRepeat: 1, rangeRepeat: 1, gapMode: 'off', rate });
    set({ range: null, ayahPass: 0, rangePass: 0 });
    get().skipGap();
  },
  skipGap: () => {
    if (get().status !== 'gap') return;
    gapAudio?.pause();
    const go = pendingGap;
    if (go) go(); else resume();
  },
}));

/* Keep the lock-screen scrubber in sync. */
if (typeof window !== 'undefined') {
  setInterval(() => {
    const st = usePlayer.getState();
    if (st.status !== 'playing' || !audio || !('mediaSession' in navigator) || !audio.duration) return;
    try { navigator.mediaSession.setPositionState({ duration: audio.duration, position: Math.min(audio.currentTime, audio.duration), playbackRate: audio.playbackRate }); } catch {}
  }, 2000);
  /* rAF stops while hidden; keep enforcing repeat boundaries via timeupdate
     so locked-screen repeat/gap still works. */
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) el().addEventListener('timeupdate', hiddenTick);
    else { el().removeEventListener('timeupdate', hiddenTick); if (usePlayer.getState().status === 'playing') schedule(); }
  });
}
function hiddenTick() { if (document.hidden) tick(); }

if (import.meta.env.DEV && typeof window !== 'undefined') (window as unknown as { __player: typeof usePlayer }).__player = usePlayer;
